import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLayout } from '../../contexts/LayoutContext';
import { useMenuSettings, MenuVisibility } from '../../contexts/MenuSettingsContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { useBranding } from '../../contexts/BrandingContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { useView } from '../../contexts/ViewContext';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Menu,
  X,
  Factory,
  Settings,
  Users,
  Mail,
  BarChart3,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { buildNavigation, type NavItem } from '../../config/navigation';
import { coreAPI } from '../../lib/api';

/**
 * Main application sidebar with role-aware navigation.
 * Renders supplier, customer, or compliance navigation items based on the
 * user's platform, portal mode, feature flags, menu visibility settings, and module permissions.
 * Supports collapsible desktop and slide-out mobile layouts.
 */
export default function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarCollapsed: collapsed, toggleSidebar } = useLayout();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['Category']); // Category expanded by default
  const [version, setVersion] = useState<string>('v1.0.0');
  const { user } = useAuth();
  const { isMenuItemVisible, isFeatureEnabled, isComplianceMode, isLoading: menuSettingsLoading } = useMenuSettings();
  const { hasModuleAccess } = useUserPermissions();
  const { branding } = useBranding();
  const { portalMode, supportsDualMode, isCustomer, isSupplierPortalSupplier } = usePortalMode();

  // Fetch version on mount
  useEffect(() => {
    coreAPI.getVersion()
      .then((res) => setVersion(`v${res.data.version}`))
      .catch(() => {
        // Keep default version on error
      });
  }, []);

  // Check if user is Helix admin (HELIX platform OR Django staff/superuser)
  const isHelixAdmin = user?.platform === 'HELIX' || user?.is_staff || user?.is_superuser || false;

  // Check if user is Django staff or superuser
  const isDjangoAdmin = user?.is_staff || user?.is_superuser || false;
  // Supplier Admin: a non-staff user whose company role has is_admin_role=True.
  // The backend UserSerializer surfaces this as the top-level `is_company_admin`
  // flag (derived from profile.role.is_admin_role) — NOT under user.role, which
  // is only the role's primary key.
  // Customer (backend company_type='customer' — the operating org's supplier):
  // admin tooling (Admin Setup / Login As / User Management) is supplier-side
  // only. Role flags like is_admin_role/can_manage_users exist on customer
  // roles too, so gate on portal side as well — the backend already 403s
  // these surfaces for customers.
  const isSupplierAdmin = !!user?.is_company_admin && !isCustomer;
  // Match UsersManagement.tsx canManageUsers check. `can_manage_users` is a
  // top-level flag on the serialized user (from profile.role.can_manage_users);
  // reading it off user.role (a numeric id) always yielded undefined, which is
  // why Supplier Admins lost Login As / User Management.
  const canManageUsers = isDjangoAdmin || isHelixAdmin ||
    (!!user?.can_manage_users && !isCustomer) || false;

  // Admin Setup is open to Django superusers and Supplier Admins (role
  // is_admin_role). Email Queue stays superuser-only (platform-level tooling).
  const { enabledModules } = useView();

  // Map visibility keys to module access keys
  const getModuleForVisibilityKey = (key: keyof MenuVisibility): 'purchasing' | 'quality' | 'finance' | null => {
    switch (key) {
      case 'purchasing':
      case 'purchaseOrders':
        return 'purchasing';
      case 'quality':
        return 'quality';
      // Finance module can be added here when needed
      default:
        return null;
    }
  };

  // Filter navigation items based on visibility settings AND user module permissions
  const filterNavigation = (items: NavItem[], isCustomerSidebar: boolean): NavItem[] => {
    return items.filter(item => {
      // Per-company module restriction — if enabledModules is set, only show listed modules.
      // This uses the original visibilityKey (purchasing / quality / dashboard / …) because
      // the tenant's enabled-module allowlist is keyed on those, not on per-role overrides.
      if (enabledModules && item.visibilityKey && !enabledModules.includes(String(item.visibilityKey))) return false;

      if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;

      if (!item.visibilityKey) return true;

      if (item.visibilityKey === 'adminSetup' && (isDjangoAdmin || isSupplierAdmin)) return true;

      // Site Performance is supplier-ADMIN only (backend 403s non-admins too).
      // Hide it from regular supplier users; the platform-settings toggle below
      // still applies for admins.
      if (item.visibilityKey === 'sitePerformance'
          && !(isDjangoAdmin || isHelixAdmin || isSupplierAdmin)) return false;

      // Exchange Rates is an operational monitor — same admin-only rule, and
      // the rate-history endpoint 403s non-admins.
      if (item.visibilityKey === 'exchangeRates'
          && !(user?.is_superuser || user?.platform === 'HELIX' || isSupplierAdmin)) return false;

      // Decide which menu-visibility key drives the on/off toggle. For a
      // customer sidebar, prefer customerVisibilityKey (customerDashboard,
      // customerQuestionnaires, customerSuppliers, …) so the supplier admin's
      // dashboard/suppliers toggle isn't shared with the customer side.
      const effectiveKey: keyof MenuVisibility = (isCustomerSidebar && item.customerVisibilityKey)
        ? item.customerVisibilityKey
        : item.visibilityKey;
      if (!isMenuItemVisible(effectiveKey)) return false;

      const module = getModuleForVisibilityKey(item.visibilityKey);
      if (module && !hasModuleAccess(module)) return false;

      return true;
    });
  };

  // Get platform-wide main menu type setting
  const { mainMenuType } = useMenuSettings();

  // Logistics users get a restricted single-page view
  const isLogisticsUser = user?.company_type === 'logistics';

  // Select navigation based on live portal mode (reacts to supplier/customer toggle),
  // compliance flag, and feature flags.
  const getNavigationForUser = (): NavItem[] => {
    // Logistics users only see dispatched call-offs
    if (isLogisticsUser) {
      return buildNavigation({ portalMode: 'supplier', isLogistics: true });
    }

    // For admin users without dual mode, respect mainMenuType platform setting
    const isAdminUser = user?.is_superuser || user?.is_staff;
    // Map ViewContext portalMode to navigation portalMode ('supplier_manager' -> 'supplier')
    // In supplier_portal mode, the customer-supplier gets the SUPPLIER admin nav (with flipped labels)
    let navPortalMode: 'supplier' | 'customer' =
      (isAdminUser && !supportsDualMode && mainMenuType === '2')
        ? 'customer'
        : portalMode === 'customer' ? 'customer' : 'supplier';

    // Supplier Portal (non-dual-mode): platform supplier/admin gets supplier nav.
    // Dual-mode Poeton sites honour the customer/supplier toggle.
    if (isSupplierPortalSupplier && !supportsDualMode) {
      navPortalMode = 'supplier';
    }

    return buildNavigation({
      portalMode: navPortalMode,
      isCompliance: isComplianceMode,
      isSupplierPortalSupplier,
    });
  };

  const baseNavigation = getNavigationForUser();
  // Work out whether we're rendering the customer sidebar. Helix admins with
  // mainMenuType=2 are also viewing the customer-side sidebar.
  const isAdminUser = user?.is_superuser || user?.is_staff;
  const isCustomerSidebar = (isAdminUser && !supportsDualMode && mainMenuType === '2')
    ? true
    : isCustomer;
  let navigation = filterNavigation(baseNavigation, isCustomerSidebar);

  // Customer sidebar gating:
  //   * Pre-approval (customer_is_approved === false): only show My Onboarding.
  //   * Post-approval: hide My Onboarding — their questionnaires live under
  //     the regular Questionnaires menu, so the onboarding page is no longer
  //     relevant and becomes dead real estate if kept.
  // Customer sidebar gating applies to native customer companies only — not
  // dual-mode supplier sites toggled into customer view (Poeton intra-site).
  const isNativeCustomerUser =
    (user?.company_type === 'customer' || user?.platform === 'CUSTOMER')
    && !supportsDualMode;
  const customerApproved = user?.customer_is_approved;
  // Pre-approval (is_approved=false): only the onboarding wizard is useful.
  // Post-approval: the My Onboarding nav entry becomes dead real estate —
  // hide it. But other items that reuse /onboarding/my-progress as their
  // href (e.g. Questionnaires, which lists the customer's FilledDatasets)
  // must survive this filter, so we match by name — not href.
  if (isNativeCustomerUser && customerApproved === false) {
    navigation = navigation.filter(item => item.name === 'My Onboarding');
  } else if (isNativeCustomerUser && customerApproved === true) {
    navigation = navigation.filter(item => item.name !== 'My Onboarding');
  }

  const isActive = (href: string) => {
    // Check for exact match first
    if (pathname === href) return true;

    // Special handling for /customers vs /customers/performance
    // Don't let /customers match if we're on /customers/performance
    if (href === '/customers' && pathname.startsWith('/customers/performance')) {
      return false;
    }

    // Special handling for /admin vs /admin/* sub-routes
    // Don't let /admin match if we're on any admin sub-route
    if (href === '/admin' && pathname.startsWith('/admin/')) {
      return false;
    }

    // Check if current path is a sub-route of the nav item
    // But only if it's actually a sub-route (has a / after the base path)
    if (href !== '/dashboard' && href !== '/') {
      // Check if pathname starts with href followed by a slash
      if (pathname.startsWith(href + '/')) {
        return true;
      }
    }

    return false;
  };

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const NavLink = ({ item, isChild = false }: { item: NavItem; isChild?: boolean }) => {
    const active = isActive(item.href);
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);

    // For items with children, render expandable section
    if (hasChildren && !collapsed) {
      return (
        <div>
          {/* Parent item with expand toggle */}
          <div
            className={cn(
              'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group cursor-pointer',
              active
                ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30',
            )}
            onClick={() => toggleExpanded(item.name)}
          >
            <Icon className={cn(
              'h-5 w-5 flex-shrink-0 transition-all duration-200 mr-3',
              active ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
            )} />
            <span className={cn(
              "font-medium transition-all duration-200 flex-1",
              active ? "text-primary font-semibold" : "group-hover:text-primary/70"
            )}>
              {item.name}
            </span>
            {/* Expand/Collapse chevron */}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {/* Children items */}
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-2">
              {/* Link to parent page */}
              <Link
                to={item.href}
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-1.5 text-sm rounded-md transition-all duration-200 group',
                  isActive(item.href)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span className="text-xs">View All</span>
              </Link>
              {item.children!.map((child) => (
                <NavLink key={child.name} item={child} isChild={true} />
              ))}
            </div>
          )}
        </div>
      );
    }

    // Standard link rendering
    return (
      <Link
        to={item.href}
        data-tour={item.tourId}
        onClick={() => {
          if (window.innerWidth < 768) {
            setMobileMenuOpen(false);
          }
        }}
        className={cn(
          'flex items-center text-sm font-medium rounded-md transition-all duration-200 relative group',
          isChild ? 'px-3 py-1.5' : 'px-3 py-2',
          active
            ? isChild
              ? 'bg-primary/10 text-primary font-medium'
              : 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
            : isChild
              ? 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30',
        )}
      >
        <Icon className={cn(
          'flex-shrink-0 transition-all duration-200',
          isChild ? 'h-4 w-4' : 'h-5 w-5',
          collapsed ? 'mr-0' : 'mr-3',
          active ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
        )} />
        {!collapsed && (
          <span className={cn(
            "transition-all duration-200",
            isChild ? "text-sm" : "font-medium",
            active ? "text-primary font-semibold" : "group-hover:text-primary/70"
          )}>
            {item.name}
          </span>
        )}
        {/* Active indicator dot */}
        {active && !collapsed && !isChild && (
          <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-card shadow-lg border border-border hover:bg-accent transition-colors"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        data-tour="sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-200 ease-in-out',
          'md:translate-x-0',
          collapsed ? 'md:w-20' : 'md:w-64',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col border-r border-border bg-card/95 backdrop-blur-sm">
          {/* Header */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            <Link to="/dashboard" className="flex items-center min-w-0">
              {branding.companyLogo ? (
                <img
                  src={branding.companyLogo}
                  alt={branding.companyName}
                  className="h-10 max-w-[160px] object-contain flex-shrink-0"
                />
              ) : (
                <>
                  <Factory className="h-8 w-8 text-primary flex-shrink-0" />
                  <span className={cn(
                    "ml-2 text-xl font-bold truncate",
                    collapsed && "hidden md:hidden"
                  )}>
                    {branding.companyName}
                  </span>
                </>
              )}
            </Link>
            <button
              type="button"
              className="hidden md:flex p-1 rounded-md hover:bg-accent"
              onClick={toggleSidebar}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-muted-foreground" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 p-3 overflow-y-auto">
            {menuSettingsLoading ? (
              /* Skeleton placeholder to prevent nav flicker during settings load */
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="flex items-center px-3 py-2 gap-3">
                    <div className="h-5 w-5 bg-muted rounded flex-shrink-0" />
                    {!collapsed && <div className="h-4 bg-muted rounded flex-1" />}
                  </div>
                ))}
              </div>
            ) : (
              navigation.map((item) => (
                <NavLink key={item.name} item={item} />
              ))
            )}
          </nav>

          {/* Footer */}
          <div className="border-t border-border p-3 space-y-1">
            {/* Admin Setup - Superusers and Supplier Admins (role is_admin_role) */}
            {(user?.is_superuser || isSupplierAdmin) && (
              <Link
                to="/admin/setup"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/setup')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Settings className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/setup') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/setup') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Admin Setup
                  </span>
                )}
              </Link>
            )}

            {/* Platform Admin - Helix staff/superusers only */}
            {(user?.is_superuser || user?.is_staff) && (
              <Link
                to="/admin/platform"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/platform')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Settings className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/platform') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/platform') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Platform Admin
                  </span>
                )}
              </Link>
            )}

            {/* Platform Analytics - Helix staff, superusers, and supplier company admins */}
            {(user?.is_superuser || user?.is_staff || isSupplierAdmin) && (
              <Link
                to="/admin/platform-analytics"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/platform-analytics')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <BarChart3 className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/platform-analytics') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/platform-analytics') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Platform Analytics
                  </span>
                )}
              </Link>
            )}

            {/* Settings */}
            <Link
              to="/settings"
              onClick={() => {
                if (window.innerWidth < 768) {
                  setMobileMenuOpen(false);
                }
              }}
              className={cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                isActive('/settings')
                  ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
              )}
            >
              <Settings className={cn(
                'h-5 w-5 flex-shrink-0 transition-all duration-200',
                collapsed ? 'mr-0' : 'mr-3',
                isActive('/settings') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
              )} />
              {!collapsed && (
                <span className={cn(
                  "font-medium transition-all duration-200",
                  isActive('/settings') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                )}>
                  My Account
                </span>
              )}
              {isActive('/settings') && !collapsed && (
                <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
              )}
            </Link>

            {/* Login As moved into the User Management list (a per-row button
                next to each email), so the standalone sidebar entry is removed
                to avoid a redundant nav item. The /admin/login-as route still
                exists for deep links. */}

            {/* Users Management - For users who can manage users */}
            {canManageUsers && (
              <Link
                to="/admin/users"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/users')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Users className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/users') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/users') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    User Management
                  </span>
                )}
                {/* Active indicator dot */}
                {isActive('/admin/users') && !collapsed && (
                  <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
                )}
              </Link>
            )}

            {/* Email Queue - Django staff / superusers (platform mail monitor) */}
            {(user?.is_staff || user?.is_superuser) && (
              <Link
                to="/admin/email-queue"
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                  isActive('/admin/email-queue')
                    ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
                )}
              >
                <Mail className={cn(
                  'h-5 w-5 flex-shrink-0 transition-all duration-200',
                  collapsed ? 'mr-0' : 'mr-3',
                  isActive('/admin/email-queue') ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
                )} />
                {!collapsed && (
                  <span className={cn(
                    "font-medium transition-all duration-200",
                    isActive('/admin/email-queue') ? "text-primary font-semibold" : "group-hover:text-primary/70"
                  )}>
                    Email Queue
                  </span>
                )}
                {/* Active indicator dot */}
                {isActive('/admin/email-queue') && !collapsed && (
                  <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
                )}
              </Link>
            )}

            <Link
              to="/help"
              data-tour="help"
              onClick={() => {
                if (window.innerWidth < 768) {
                  setMobileMenuOpen(false);
                }
              }}
              className={cn(
                'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative group',
                pathname === '/help'
                  ? 'bg-primary/15 text-primary border-l-4 border-primary pl-2 shadow-sm ring-1 ring-primary/20 font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground border-l-4 border-transparent hover:border-l-2 hover:border-primary/30'
              )}
            >
              <HelpCircle className={cn(
                'h-5 w-5 flex-shrink-0 transition-all duration-200',
                collapsed ? 'mr-0' : 'mr-3',
                pathname === '/help' ? 'text-primary drop-shadow-sm' : 'text-muted-foreground group-hover:text-primary/70'
              )} />
              {!collapsed && (
                <span className={cn(
                  "font-medium transition-all duration-200",
                  pathname === '/help' ? "text-primary font-semibold" : "group-hover:text-primary/70"
                )}>
                  Help & Support
                </span>
              )}
              {/* Active indicator dot for help */}
              {pathname === '/help' && !collapsed && (
                <div className="absolute right-3 w-2 h-2 bg-primary rounded-full shadow-sm animate-pulse" />
              )}
            </Link>

            {/* Version display (changelog popup disabled). */}
            {!collapsed && (
              <div className="mt-3 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground w-full text-left px-3 block">
                  {version}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
